import * as React from 'react';

import {Illustration} from 'shells/illustrations/illustration';
import {PageHeaderFull} from 'shells/layout/page-header-full';

import {PageTitleBar} from '../layout';

import {SettingsNavNew} from './nav/settings-nav-new';
import type {ServerTitleBarProps} from './setup-root';
import type {TitleBarProps} from './types';

// Specific setup pages that are allowed for a given permission, ordered by priority(!!)
const ALLOWED_PERMISSION_SETUP_PATHS = {
    canAccessBilling: ['/setup/billing/sales'],
    canImport: ['/setup/import', '/setup/(import.*)'],
    canAccessMarketing: [],
    canDeleteEntities: ['/trash'],
} as const;

// We do not want to show the sidebar when editing a sales automation pipeline or when managing billing
const STAGESET_REGEX = /setup\/sales-automation\/[\d]+-stagesets/;
const BILLING_REGEX =
    /setup\/billing\/(sales|marketing|engagement|add-ons|review|success|churn|cancel)/;

/**
 * Returns true if the user's permissions grant access to at least one setup page.
 *
 * @param userPermissions
 * @returns {boolean}
 */
// @ts-expect-error
export const userCanAccessSetup = (userPermissions?: UserPermissions | null): boolean => {
    if (!userPermissions) {
        return false;
    }

    if (userPermissions.canAccessSetup) {
        return true;
    }

    let isAllowed = false;
    Object.keys(ALLOWED_PERMISSION_SETUP_PATHS).forEach((permission) => {
        if (userPermissions[permission]) {
            isAllowed = true;
        }
    });

    return isAllowed;
};

/**
 * Returns true if the user's permissions grant access to the given setup page.
 *
 * @param userPermissions
 * @param pagePath
 * @returns {boolean}
 */
export const userCanAccessSetupPage = (
    // @ts-expect-error
    userPermissions: UserPermissions | null | undefined,
    pagePath: string
): boolean => {
    if (!userPermissions) {
        return false;
    }

    if (userPermissions.canAccessSetup) {
        return true;
    }

    let isAllowed = false;
    Object.keys(ALLOWED_PERMISSION_SETUP_PATHS).forEach((permission) => {
        if (userPermissions[permission]) {
            // @ts-expect-error
            ALLOWED_PERMISSION_SETUP_PATHS[permission].forEach((allowedPath) => {
                if (pagePath.match(allowedPath) || pagePath === allowedPath) {
                    isAllowed = true;
                }
            });
        }
    });

    return isAllowed;
};

/**
 * Returns the path to the first setup page that the user's permissions grant access to, undefined if none.
 *
 * @param userPermissions
 * @returns {string}
 */
export const getFirstAccessibleSetupPathForUser = (
    // @ts-expect-error
    userPermissions?: UserPermissions | null
): string | null | undefined => {
    if (userPermissions) {
        if (userPermissions.canAccessSetup) {
            return '/setup/index';
        }

        // @ts-expect-error
        let firstAllowedPath;
        Object.keys(ALLOWED_PERMISSION_SETUP_PATHS).forEach((permission) => {
            // @ts-expect-error
            if (userPermissions[permission] && !firstAllowedPath) {
                // first allowed setup path for the permission
                // @ts-expect-error
                firstAllowedPath = ALLOWED_PERMISSION_SETUP_PATHS[permission][0];
            }
        });

        return firstAllowedPath;
    }
};

export const getBestDashboardRedirectPath = (
    // @ts-expect-error
    userPermissions?: UserPermissions | null
): string | null | undefined => {
    if (
        NutClientConfig.hasTrialOnboarding &&
        NutClientConfig.isTrial &&
        NutClientConfig.loggedInUserIsAdmin
    ) {
        return '/explore';
    }

    if (userPermissions) {
        if (userPermissions.canAccessCrm) {
            return '/sales/dashboard';
        } else if (userPermissions.canAccessMarketing) {
            return '/marketing/dashboard';
        } else if (userPermissions.canUseOrTryInbox) {
            return '/inbox/dashboard';
        }
    }

    // Should not hit this case- user should not have been able to log in without access to a suite
    return null;
};

export const getSetupSidebar = (
    pathname: string,
    isPaused: boolean,
    isDisabled: boolean
): React.ReactNode | null | undefined => {
    return STAGESET_REGEX.test(pathname) || BILLING_REGEX.test(pathname) ? null : (
        <SettingsNavNew paused={isPaused} disabled={isDisabled} />
    );
};

export const getSetupPageHeader = (
    pathname: string,
    titles?: TitleBarProps | null,
    serverTitleBarValues?: ServerTitleBarProps | null
): React.ReactNode | null | undefined => {
    if (!titles) {
        return null;
    }

    switch (pathname) {
        case '/setup/tracking-snippet':
        case '/setup/domains':
            return (
                <PageHeaderFull
                    title={titles.pageTitle}
                    // @ts-expect-error
                    subtitle={titles.pageSubTitle}
                    glossaryKey={titles.glossaryKey}
                    illustration={getPageHeaderIllustration(pathname)}
                    illustrationAlignment={getPageHeaderIllustrationAlignment(pathname)}
                    height={280}
                    xPadding={40}
                />
            );
        case '/setup/ai-features':
            if (!NutClientConfig.hasAiOutcomesFF) {
                return (
                    <PageTitleBar
                        title={
                            (serverTitleBarValues && serverTitleBarValues.pageTitle) ||
                            titles.pageTitle
                        }
                        subheaderPrimaryText={
                            serverTitleBarValues && serverTitleBarValues.pageSubtitle
                                ? serverTitleBarValues.pageSubtitle
                                : titles.pageSubTitle || 'Only admins can view this page'
                        }
                        titleBadge={titles.limits}
                        buttons={titles.buttons}
                        breadcrumbs={titles.breadcrumbs}
                        glossaryKey={titles.glossaryKey}
                    />
                );
            }

            return (
                <PageHeaderFull
                    title={titles.pageTitle}
                    // @ts-expect-error
                    subtitle={titles.pageSubTitle}
                    glossaryKey={titles.glossaryKey}
                    illustration={getPageHeaderIllustration(pathname)}
                    illustrationAlignment={getPageHeaderIllustrationAlignment(pathname)}
                    backgroundColorVariant='purple'
                    height={280}
                    xPadding={40}
                />
            );
        default:
            return (
                <PageTitleBar
                    title={
                        (serverTitleBarValues && serverTitleBarValues.pageTitle) || titles.pageTitle
                    }
                    subheaderPrimaryText={
                        serverTitleBarValues && serverTitleBarValues.pageSubtitle
                            ? serverTitleBarValues.pageSubtitle
                            : titles.pageSubTitle || 'Only admins can view this page'
                    }
                    titleBadge={titles.limits}
                    buttons={titles.buttons}
                    breadcrumbs={titles.breadcrumbs}
                    glossaryKey={titles.glossaryKey}
                />
            );
    }
};

const getPageHeaderIllustration = (pathname: string): React.ReactElement => {
    switch (pathname) {
        case '/setup/domains':
            return <Illustration src='domains-hero' width={380} />;
        case '/setup/tracking-snippet':
            return <Illustration src='tracking-snippet-hero' width={380} />;
        case '/setup/ai-features':
            return <Illustration src='ai-features' width={380} />;
        default:
            return <Illustration src='domains-hero' width={380} />;
    }
};

const getPageHeaderIllustrationAlignment = (pathname: string): 'center' | 'end' | 'start' => {
    switch (pathname) {
        case '/setup/domains':
        case '/setup/tracking-snippet':
        case '/setup/ai-features':
            return 'end';
        default:
            return 'center';
    }
};
